// Bake a model with its {@link Socket}s FILLED — the model's variant parts plus
// each supplied content model placed at its socket — into one flat, static
// {@link Part} list. This is the socket analogue of `bakeInteraction`: where
// that places a *posed actor* at a {@link Station}, this places *static content*
// at a socket, so a renderer can show an avatar actually carrying a good (the
// preview and tests use it) without the avatar ever importing that good. The
// model publishes the anchor; the consumer supplies what hangs on it.
//
// Geometry is pre-transformed (each part's geometry is cloned and the composed
// world matrix applied) and returned with identity transforms, so `buildModel`
// re-derives flat shading in the final placement — matching `bakeInteraction`.

import * as THREE from "three";
import type { Model, Part, Socket } from "./types";
import { partMatrix } from "./build-model";

const IDENTITY = new THREE.Matrix4();

/** The variant a model shows: the requested one, else its default, else first. */
function variantParts(model: Model, variantId?: string): Part[] {
  const v =
    (variantId ? model.variants.find((x) => x.id === variantId) : undefined) ??
    model.variants.find((x) => x.id === model.defaultVariantId) ??
    model.variants[0]!;
  return v.parts();
}

/** The 4x4 placing mounted content at a socket: T(position)·R·S(scale).
 *  Exported for composers that mount content while keeping the host's own
 *  part names posable (the clip engine's walking, carrying actor). */
export function socketMatrix(s: Socket): THREE.Matrix4 {
  const m = new THREE.Matrix4().makeTranslation(s.position[0], s.position[1], s.position[2]);
  if (s.rotation) {
    m.multiply(new THREE.Matrix4().makeRotationFromEuler(
      new THREE.Euler(s.rotation[0], s.rotation[1], s.rotation[2]),
    ));
  }
  if (s.scale !== undefined && s.scale !== 1) {
    m.multiply(new THREE.Matrix4().makeScale(s.scale, s.scale, s.scale));
  }
  return m;
}

/** Clone a part's geometry into world space, returning a transform-free part so
 *  `buildModel` shades it in its final placement. */
function bakedPart(part: Part, world: THREE.Matrix4): Part {
  const m = world.clone().multiply(partMatrix(part));
  return {
    geometry: part.geometry.clone().applyMatrix4(m),
    color: part.color,
    ...(part.slot !== undefined ? { slot: part.slot } : {}),
  };
}

/** One filled socket: the `model` (at `variantId`) mounted at the socket `socket`. */
export interface SocketFill {
  /** The {@link Socket} (by id) to mount the content at. */
  socket: string;
  /** The model whose parts are mounted there. */
  model: Model;
  /** Which variant of the content to mount (default: its default variant). */
  variantId?: string;
}

export interface BakeSocketsOptions {
  /** Which variant of the host model to show (default: its default variant). */
  variantId?: string;
}

/**
 * Flatten `model` with its `fills` into one {@link Part} list: the model's own
 * parts plus each fill's content model placed at the named {@link Socket} (its
 * position, optional rotation and scale). Throws a teaching error if a fill
 * references a socket the model does not define. Pure and three.js-only — no
 * scene graph.
 */
export function bakeSockets(
  model: Model,
  fills: SocketFill[],
  opts: BakeSocketsOptions = {},
): Part[] {
  const out: Part[] = [];

  // The host model itself, untransformed.
  for (const p of variantParts(model, opts.variantId)) out.push(bakedPart(p, IDENTITY));

  // Compute dynamic layout overrides when the model provides a layout callback.
  const overrides = model.socketLayout?.(
    fills.map((f) => ({ socketId: f.socket, model: f.model })),
  ) ?? {};

  // Each filled socket: the content model baked at the socket's placement.
  for (const fill of fills) {
    const socket = model.sockets.find((s) => s.id === fill.socket);
    if (!socket) {
      throw new Error(
        `Model "${model.id}" has no socket "${fill.socket}". ` +
        `Known sockets: ${model.sockets.map((s) => s.id).join(", ") || "(none)"}.`,
      );
    }
    const ovr = overrides[fill.socket];
    const effective: Socket = ovr
      ? {
          ...socket,
          ...(ovr.position ? { position: ovr.position } : {}),
          ...(ovr.rotation ? { rotation: ovr.rotation } : {}),
          ...(ovr.scale !== undefined ? { scale: ovr.scale } : {}),
        }
      : socket;
    const world = socketMatrix(effective);
    for (const p of variantParts(fill.model, fill.variantId)) out.push(bakedPart(p, world));
  }
  return out;
}
